import type {
  AppSettings,
  CalibrationProfile,
  EnvironmentSnapshot,
  ModuleSummary,
  SessionCompletionPayload,
  SessionMode,
  SessionRecord,
  TrialResult,
} from '../core/types';
import { assessmentSummariesAreComplete, MODULE_IDS, PROTOCOL_VERSION } from '../core/protocols';
import { Scan90Database, scan90Database } from './database';
import { createDefaultSettings, mergeSettings } from './settings';
import type { StoredModuleSummary } from './types';

export interface ListSessionOptions {
  status?: SessionRecord['status'];
  mode?: SessionMode;
  includeIncomplete?: boolean;
}

function storedSummary(sessionId: string, summary: ModuleSummary): StoredModuleSummary {
  return {
    ...summary,
    id: `${sessionId}:${summary.moduleId}`,
    sessionId,
  };
}

function withoutStorageKeys(summary: StoredModuleSummary): ModuleSummary {
  const { id: _id, sessionId: _sessionId, ...value } = summary;
  return value;
}

export class Scan90Repository {
  constructor(readonly database: Scan90Database = scan90Database) {}

  async getSettings(): Promise<AppSettings> {
    const record = await this.database.settings.get('app');
    return record ? mergeSettings(createDefaultSettings(), record.value) : createDefaultSettings();
  }

  async saveSettings(update: Partial<AppSettings> | AppSettings): Promise<AppSettings> {
    const current = await this.getSettings();
    const value = mergeSettings(current, update);
    await this.database.settings.put({ key: 'app', value, updatedAt: new Date().toISOString() });
    return value;
  }

  async saveCalibration(profile: CalibrationProfile): Promise<CalibrationProfile> {
    await this.database.calibrations.put(profile);
    return profile;
  }

  getCalibration(id: string): Promise<CalibrationProfile | undefined> {
    return this.database.calibrations.get(id);
  }

  getLatestCalibration(): Promise<CalibrationProfile | undefined> {
    return this.database.calibrations.orderBy('createdAt').last();
  }

  async saveEnvironment(snapshot: EnvironmentSnapshot): Promise<EnvironmentSnapshot> {
    await this.database.environments.put(snapshot);
    return snapshot;
  }

  getEnvironment(id: string): Promise<EnvironmentSnapshot | undefined> {
    return this.database.environments.get(id);
  }

  getLatestEnvironment(calibrationId?: string): Promise<EnvironmentSnapshot | undefined> {
    if (!calibrationId) return this.database.environments.orderBy('capturedAt').last();
    return this.database.environments.where('calibrationId').equals(calibrationId).toArray().then(
      (items) => items.sort((left, right) => left.capturedAt.localeCompare(right.capturedAt)).at(-1),
    );
  }

  async saveCalibrationBundle(
    profile: CalibrationProfile,
    snapshot: EnvironmentSnapshot,
  ): Promise<AppSettings> {
    if (snapshot.calibrationId !== profile.id) {
      throw new Error('Environment snapshot must reference the calibration profile');
    }
    return this.database.transaction(
      'rw',
      this.database.calibrations,
      this.database.environments,
      this.database.settings,
      async () => {
        const currentRecord = await this.database.settings.get('app');
        const current = currentRecord?.value ?? createDefaultSettings();
        const value = mergeSettings(current, { calibrationId: profile.id, onboardingComplete: true });
        await this.database.calibrations.put(profile);
        await this.database.environments.put(snapshot);
        await this.database.settings.put({ key: 'app', value, updatedAt: new Date().toISOString() });
        return value;
      },
    );
  }

  async saveSession(session: SessionRecord): Promise<SessionRecord> {
    await this.database.sessions.put(session);
    return session;
  }

  async saveAbortedSession(session: SessionRecord, trials: TrialResult[]): Promise<SessionRecord> {
    if (trials.some((trial) => trial.sessionId !== session.id)) {
      throw new Error('Aborted session contains a trial from another session');
    }
    const aborted: SessionRecord = {
      ...session,
      status: 'aborted',
      completedAt: undefined,
      trialIds: trials.map((trial) => trial.id),
    };
    await this.database.transaction(
      'rw',
      this.database.sessions,
      this.database.trials,
      async () => {
        await this.database.sessions.put(aborted);
        if (trials.length) await this.database.trials.bulkPut(trials);
      },
    );
    return aborted;
  }

  getSession(id: string): Promise<SessionRecord | undefined> {
    return this.database.sessions.get(id);
  }

  async listSessions(options: ListSessionOptions = {}): Promise<SessionRecord[]> {
    let sessions = await this.database.sessions.orderBy('startedAt').reverse().toArray();
    if (options.status) sessions = sessions.filter((session) => session.status === options.status);
    if (options.mode) sessions = sessions.filter((session) => session.mode === options.mode);
    if (options.includeIncomplete === false) {
      sessions = sessions.filter((session) => session.status === 'completed');
    }
    return sessions;
  }

  async addTrial(result: TrialResult): Promise<TrialResult> {
    await this.database.transaction('rw', this.database.sessions, this.database.trials, async () => {
      await this.database.trials.put(result);
      const session = await this.database.sessions.get(result.sessionId);
      if (!session) throw new Error(`Session not found: ${result.sessionId}`);
      if (!session.trialIds.includes(result.id)) {
        await this.database.sessions.update(session.id, { trialIds: [...session.trialIds, result.id] });
      }
    });
    return result;
  }

  async getSessionTrials(sessionId: string): Promise<TrialResult[]> {
    const trials = await this.database.trials.where('sessionId').equals(sessionId).toArray();
    return trials.sort((left, right) => left.startedAt.localeCompare(right.startedAt));
  }

  async getSessionSummaries(sessionId: string): Promise<ModuleSummary[]> {
    const summaries = await this.database.summaries.where('sessionId').equals(sessionId).toArray();
    return summaries.map(withoutStorageKeys);
  }

  async saveSessionCompletion(payload: SessionCompletionPayload): Promise<SessionRecord> {
    const session: SessionRecord = {
      ...payload.session,
      status: 'completed',
      completedAt: payload.session.completedAt ?? new Date().toISOString(),
      trialIds: payload.trials.map((trial) => trial.id),
    };
    for (const trial of payload.trials) {
      if (trial.sessionId !== session.id) {
        throw new Error(`Trial ${trial.id} belongs to another session`);
      }
    }

    await this.database.transaction(
      'rw',
      this.database.sessions,
      this.database.trials,
      this.database.summaries,
      async () => {
        await this.database.sessions.put(session);
        await this.database.trials.bulkPut(payload.trials);
        await this.database.summaries.where('sessionId').equals(session.id).delete();
        await this.database.summaries.bulkPut(
          payload.summaries.map((summary) => storedSummary(session.id, summary)),
        );
      },
    );
    return session;
  }

  async abortSession(sessionId: string): Promise<void> {
    const updated = await this.database.sessions.update(sessionId, {
      status: 'aborted',
      completedAt: undefined,
    });
    if (updated === 0) throw new Error(`Session not found: ${sessionId}`);
  }

  async getBaselineStatus(): Promise<{ baselineA: boolean; baselineB: boolean; complete: boolean }> {
    const completed = await this.listSessions({ status: 'completed' });
    const formIsComplete = async (mode: 'baseline-a' | 'baseline-b'): Promise<boolean> => {
      const candidates = completed.filter((session) =>
        session.mode === mode
        && session.protocolVersion === PROTOCOL_VERSION
        && session.moduleIds.length === MODULE_IDS.length
        && new Set(session.moduleIds).size === MODULE_IDS.length
        && MODULE_IDS.every((moduleId) => session.moduleIds.includes(moduleId)));
      for (const session of candidates) {
        if (assessmentSummariesAreComplete(await this.getSessionSummaries(session.id))) return true;
      }
      return false;
    };
    const [baselineA, baselineB] = await Promise.all([
      formIsComplete('baseline-a'),
      formIsComplete('baseline-b'),
    ]);
    return { baselineA, baselineB, complete: baselineA && baselineB };
  }

  async clearAll(): Promise<void> {
    await this.database.transaction(
      'rw',
      [
        this.database.calibrations,
        this.database.environments,
        this.database.sessions,
        this.database.trials,
        this.database.summaries,
        this.database.settings,
        this.database.metadata,
      ],
      async () => {
        await Promise.all([
          this.database.calibrations.clear(),
          this.database.environments.clear(),
          this.database.sessions.clear(),
          this.database.trials.clear(),
          this.database.summaries.clear(),
          this.database.settings.clear(),
          this.database.metadata.clear(),
        ]);
      },
    );
  }
}

export const repository = new Scan90Repository();
